import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Megaphone,
  Inbox,
  Mic2,
  CreditCard,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Coins,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTutorial } from "../../contexts/TutorialContext";
import LogoDisplay from "../../config/LogoDisplay";
import { subscribeNotifications } from "../../services/collavueStore";

const NAV = [
  { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard, tutorial: "nav-dashboard" },
  { to: "/campaigns", label: "Campaigns", Icon: Megaphone, tutorial: "nav-campaigns" },
  { to: "/leads", label: "Lead Inbox", Icon: Inbox, tutorial: "nav-leads" },
  { to: "/brand-voice", label: "Brand Voice", Icon: Mic2, tutorial: "nav-brand-voice" },
  { to: "/billing", label: "Billing", Icon: CreditCard },
  { to: "/notifications", label: "Notifications", Icon: Bell },
  { to: "/settings", label: "Settings", Icon: Settings },
];

export default function AppShell({ children }) {
  const { user, userProfile, logout } = useAuth();
  const tutorial = useTutorial();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    return subscribeNotifications((items) =>
      setUnread(items.filter((n) => !n.read).length)
    );
  }, [user]);

  // Auto-open the mobile sidebar while the tutorial highlights a sidebar item,
  // so the spotlight lines up with something the user can actually see.
  useEffect(() => {
    if (!tutorial?.active) return;
    const t = tutorial.step?.target;
    const sidebarTargets = new Set([
      "nav-dashboard",
      "nav-campaigns",
      "nav-leads",
      "nav-brand-voice",
      "credits-widget",
    ]);
    if (t && sidebarTargets.has(t)) setOpen(true);
  }, [tutorial?.active, tutorial?.step]);

  const credits = Number(userProfile?.credits ?? 0);
  const isAdmin = !!userProfile?.isAdmin;

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#0B1038] flex font-sans">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="px-5 py-5 border-b border-gray-200">
          <LogoDisplay size="lg" showWordmark={false} />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, Icon, tutorial }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              data-tutorial={tutorial}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-[#0B1038] text-white font-semibold"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
              {to === "/notifications" && unread > 0 && (
                <span className="ml-auto bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                  {unread}
                </span>
              )}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-[#0B1038] text-white font-semibold"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="p-3 border-t border-gray-200 space-y-2">
          <Link
            to="/billing"
            data-tutorial="credits-widget"
            className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-100 rounded-sm text-sm font-semibold text-[#0B1038]"
          >
            <span className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-blue-600" />
              Credits
            </span>
            <span className="text-blue-600 tabular-nums">{credits}</span>
          </Link>
          <div className="px-3 pt-1 pb-2 text-xs text-gray-500 truncate">
            {user?.email}
          </div>
          <button
            onClick={() => logout({ redirectTo: "/" })}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-sm"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-[#0B1038]/40 lg:hidden"
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-gray-200 flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setOpen((v) => !v)}
            className="p-2 rounded hover:bg-gray-100"
            aria-label="Open menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <LogoDisplay size="md" showWordmark={false} />
          <button
            onClick={() => nav("/billing")}
            className="text-xs px-2 py-1.5 rounded-sm bg-blue-50 text-blue-700 font-semibold tabular-nums"
          >
            {credits} credits
          </button>
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
